import React, { CSSProperties } from 'react'
import styled, { css } from 'styled-components'

import FICColors from '../../common/FICColors'
import FICAutocomplete from '../../layout_components/js/FICAutocomplete'
import { SearchResult } from '../definitions'

export const AutocompleteContainer = styled.div<{
  isExternal: boolean
  internal: boolean
  open: boolean
}>`
  height: 63px;
  width: 625px;
  max-width: 100%;
  ${(props) =>
    props.isExternal &&
    css`
      width: 780px;
    `};
  border-radius: 4px;
  background-color: ${FICColors.white};
  box-shadow: 0 9px 16px 0 rgba(0, 0, 0, 0.1);
  position: relative;
  margin-top: 15px;
  -webkit-transition: all 200ms ease-out; /* For Safari 3.1 to 6.0 */
  transition: all 200ms ease-out;
  .fa,
  .svg-inline--fa {
    font-size: 24px !important;
    color: #333 !important;
    position: absolute;
    left: 20px;
    top: 20px;
    z-index: 1000;
    &.close {
      right: 5px;
      left: auto;
      opacity: 1;
      display: none;
    }
  }
  ${(props) =>
    props.internal &&
    css`
      width: 50px;
      margin: 0;
      max-height: 100%;
      box-sizing: border-box;
      box-shadow: none;
      position: absolute;
      top: 0;
      right: 8px;
      .fa,
      .svg-inline--fa {
        cursor: pointer;
        z-index: 10000;
        ${() =>
          props.open &&
          css`
            margin-left: 0 !important;
            left: 5px;
          `}
      }
      ${() =>
        props.open &&
        css`
          right: 0;
          width: 100%;
          .fa.close,
          .svg-inline--fa.close {
            display: block;
            margin-right: 0;
          }
        `}
    `}
`

export interface AutocompleteProps {
  internal: boolean
  loadOptions: (text: string) => void
  placeholder: string
  loadOptionsDelay: number
  onChange: (
    name: string,
    value: string,
    x: string | null,
    className: string
  ) => void
  onKeyPress: (e: React.KeyboardEvent) => void
  onSelect: (label: string, data: SearchResult) => void
  open: boolean
  options: SearchResult[]
  renderItem: (
    item: SearchResult,
    className?: string,
    style?: CSSProperties
  ) => JSX.Element
  renderMenuContent: (
    items: React.ReactNode[],
    value?: string,
    style?: CSSProperties
  ) => JSX.Element
  shouldItemRender: (item: SearchResult, value: string) => boolean
  value: string
  className?: string
  autoFocus?: boolean
  changeOnBlur?: boolean
  uncontrolled?: boolean
  changeDelay?: number
  triggerLength?: 0
  clearable?: boolean
  name?: string
  preventChangeOnSelect?: boolean
  onFocus?: (e) => void
  onBlur?: (e) => void
  idKey?: string
  labelKey?: string
  maxMenuHeight?: number
  validator?: { valid: boolean }
  valueKey?: string
  disabled?: boolean
  onKeyDown?: () => void
  onKeyUp?: () => void
  onClick?: () => void
  onMouseOver?: () => void
  onMouseOut?: () => void
  autoComplete?: 'new-password'
  maxLength?: number
}

export const AutocompleteStyled = styled(FICAutocomplete)<AutocompleteProps>`
  z-index: 9999;
  height: 100%;
  input {
    display: flex;
    width: 99%;
    margin: auto;
    box-sizing: border-box;
    height: 63px;
    border: none !important;
    outline: none !important;
    padding-left: 50px !important;
    font-size: 16px !important;
    font-family: 'Open Sans', sans-serif;
    &:focus {
      box-shadow: none !important;
    }
    &::placeholder {
      font-size: 16px !important;
    }
    ${(props) =>
      props.internal &&
      css`
        height: 100%;
        padding-right: 50px;
        display: none;
      `}
    ${(props) =>
      props.open &&
      css`
        display: block;
      `}
  }
  .fic-autocomplete-menu-outer {
    margin-top: 4px !important;
  }
`

export const Option = styled.div<{ type: string }>`
  color: rgba(51, 51, 51, 0.72);
  background-color: ${(props) =>
    props.type === 'articolo' ? FICColors.white : FICColors.yellow};
  border-radius: 4px;
  padding: 12px 0 12px 40px !important;
  font-size: 16px !important;
  line-height: 24px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  &:hover {
    color: ${FICColors.defaultBlue}!important;
    background-color: ${FICColors.lightGray}!important;
  }
`
export const Results = styled.div<{ internal: boolean; open: boolean }>`
  text-align: center;
  background-color: ${FICColors.white};
  border-radius: 4px;
  box-shadow: 0 9px 16px 0 rgba(0, 0, 0, 0.1);
  margin-top: 5px;
  overflow-x: hidden;
  max-height: 500px;
  overflow-y: scroll;
  ${(props) =>
    props.internal &&
    !props.open &&
    css`
      display: none;
      color: red;
    `}
  p {
    padding-top: 10px;
    font-weight: bold;
    color: ${FICColors.textDefault};
    font-size: 16px;
    margin-bottom: 10px;
  }
  .noResults {
    p {
      padding: 20px 0;
      margin-bottom: 0;
      font-size: 20px;
      font-weight: 600;
    }
  }
  .searchResultsHeader {
    text-align: left;
    margin-left: 40px;
    text-transform: uppercase;
    padding-top: 5px;
    p {
      font-size: 12px;
      color: #0094d4 !important;
    }
  }
`

export const TopArticles = styled.div`
  z-index: 10000;
  border-top: 1px solid rgba(51, 51, 51, 0.08);
  padding: 24px 65px;
  box-sizing: border-box;
  text-align: left;
  @media (max-width: 425px) {
    padding: 16px 16px;
  }
  p {
    margin-top: 0;
    font-size: 18px;
    margin-bottom: 24px;
  }
  ul {
    padding: 0;
    -webkit-column-count: 2; /* Chrome, Safari, Opera */
    -moz-column-count: 2; /* Firefox */
    column-count: 2;
    -webkit-column-gap: 40px; /* Chrome, Safari, Opera */
    -moz-column-gap: 40px; /* Firefox */
    column-gap: 40px;
    margin-left: 10px;
    li {
      color: ${FICColors.textDefault};
      margin-bottom: 12px;
      margin-left: 10px;
      font-size: 16px;
      line-height: 25px;
      width: 100%;
      -webkit-column-break-inside: avoid;
      page-break-inside: avoid;
      break-inside: avoid;
      @media (max-width: 425px) {
        font-size: 14px;
        line-height: 20px;
      }
      span {
        cursor: pointer;
        color: ${FICColors.defaultBlue};
        &:hover {
          text-decoration: underline;
          &::before {
            text-decoration: none;
          }
        }
      }
    }
  }
`

export const Tree = styled.div`
  height: 16px;
  color: rgba(51, 51, 51, 0.72);
  font-size: 12px;
  letter-spacing: -0.13px;
  line-height: 16px;
  .fa,
  .svg-inline--fa {
    position: unset;
    font-size: 12px !important;
    margin: 0 5px !important;
    color: rgba(51, 51, 51, 0.72) !important;
  }
`
